import React from 'react';
import { readableFColor } from '../../../util/readableBrandColor';
import OolaWheelMilestoneV2 from './OolaWheelMilestoneV2';
import './SectionCompleteV2.css';

function IconCheck() {
    return (
        <svg viewBox="0 0 24 24" className="pv2-section-done__badge-svg" aria-hidden="true">
            <circle
                cx="12"
                cy="12"
                r="9"
                fill="none"
                stroke="#2fd08a"
                strokeWidth="2"
            />
            <path
                d="M8.2 12.2l2.4 2.4 5.2-5.2"
                fill="none"
                stroke="#2fd08a"
                strokeWidth="2.1"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

function IconRing() {
    return (
        <svg viewBox="0 0 24 24" className="pv2-section-done__badge-svg" aria-hidden="true">
            <circle
                cx="12"
                cy="12"
                r="8"
                fill="none"
                stroke="rgba(255,255,255,0.18)"
                strokeWidth="2.4"
            />
            <circle
                cx="12"
                cy="12"
                r="8"
                fill="none"
                stroke="#efd078"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeDasharray="14 40"
                transform="rotate(110 12 12)"
            />
        </svg>
    );
}

function IconTrend() {
    return (
        <svg viewBox="0 0 24 24" className="pv2-section-done__badge-svg" aria-hidden="true">
            <path
                d="M4.5 16.5l5-5.2 3.2 2.6 6.3-7.4"
                fill="none"
                stroke="#2fd08a"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <path
                d="M15.2 6.5h4.3v4.3"
                fill="none"
                stroke="#2fd08a"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

const FUN_FINAL_LEAD =
    "Congrats, you have completed the Fun section and your questions for all 7 F's of Oola. Your OolaWheel and OolaScore are being prepared for you now.";

/**
 * Portal V2 — single-F section complete (and Fun = Step 1 final).
 * Classic /steps stays on the original complete UI.
 */
export default function SectionCompleteV2({
    Fs = [],
    activeIndex = 0,
    completedName = 'Fitness',
    leadText,
    ctaLabel,
    onContinue,
    onSaveLater,
}) {
    const isStep1Final = activeIndex >= 6;
    const completed = Math.min(7, activeIndex + 1);
    const nextF = !isStep1Final ? Fs[activeIndex + 1] || null : null;
    const nextName = nextF?.name || 'next section';
    const accent = readableFColor(
        Fs[activeIndex]?.name || completedName,
        Fs[activeIndex]?.color || '#15c285'
    );
    const active = Fs[activeIndex] || {};

    const shownScore = (() => {
        const total =
            active.TotalScore != null
                ? Number(active.TotalScore)
                : (active.questions || []).reduce(
                      (pv, cv) =>
                          pv + (cv.score != null ? Number(cv.score) : 0),
                      0
                  );
        if (!total || Number.isNaN(total)) return null;
        return (total / 10).toFixed(1);
    })();

    const titleName = String(completedName || 'Section');
    const lead =
        leadText ||
        (isStep1Final
            ? FUN_FINAL_LEAD
            : `You completed the ${titleName} section. Your OolaWheel is taking shape, and you're one step closer to a clearer view of your life.`);

    const buttonLabel = ctaLabel
        ? ctaLabel
        : isStep1Final
          ? 'COMPLETE STEP 1'
          : nextF
            ? `Continue to ${nextName}`
            : 'Continue to next section';

    return (
        <div
            className={`pv2-section-done${
                isStep1Final ? ' pv2-section-done--step1-final' : ''
            }`}
            style={{ ['--pv2-done-accent']: accent }}
        >
            <div className="pv2-section-done__copy">
                <p className="pv2-section-done__eyebrow">
                    {isStep1Final
                        ? 'Step 1 • All 7 F’s complete'
                        : 'Step 1 • Section complete'}
                </p>

                <h2 className="pv2-section-done__title">
                    <span>
                        {titleName.toUpperCase()} Complete
                        {isStep1Final ? '!' : ''}
                    </span>
                </h2>

                <p className="pv2-section-done__lead">{lead}</p>
            </div>

            <div className="pv2-section-done__visual">
                <OolaWheelMilestoneV2
                    Fs={Fs}
                    activeIndex={activeIndex}
                    nextName={nextF?.name}
                    nextColor={nextF?.color}
                    focusScore={shownScore}
                />
            </div>

            <div className="pv2-section-done__badges" aria-label="Progress">
                <div className="pv2-section-done__badge">
                    <IconCheck />
                    <span className="pv2-section-done__badge-text">
                        <strong>{completed} of 7</strong>
                        <small>areas completed</small>
                        {shownScore ? (
                            <small className="pv2-section-done__badge-focus">
                                · {titleName} {shownScore}
                            </small>
                        ) : null}
                    </span>
                </div>
                <div className="pv2-section-done__badge">
                    <IconRing />
                    <span className="pv2-section-done__badge-text">
                        <strong>OolaWheel</strong>
                        <small>
                            {isStep1Final ? 'ready' : 'in progress'}
                        </small>
                    </span>
                </div>
                <div className="pv2-section-done__badge">
                    <IconTrend />
                    <span className="pv2-section-done__badge-text">
                        <strong>
                            {isStep1Final ? 'Step 1' : 'Momentum'}
                        </strong>
                        <small>
                            {isStep1Final ? 'ready to finish' : 'building'}
                        </small>
                    </span>
                </div>
            </div>

            <div className="pv2-section-done__actions">
                <button
                    type="button"
                    className="pv2-section-done__cta"
                    onClick={onContinue}
                >
                    {buttonLabel}
                    {!isStep1Final ? (
                        <span
                            className="pv2-section-done__cta-arrow"
                            aria-hidden="true"
                        >
                            →
                        </span>
                    ) : null}
                </button>

                {typeof onSaveLater === 'function' && !isStep1Final ? (
                    <button
                        type="button"
                        className="pv2-section-done__save"
                        onClick={onSaveLater}
                    >
                        Save and continue later
                    </button>
                ) : null}
            </div>
        </div>
    );
}
